import React, { useState, useCallback } from 'react';
import { randomUUID } from '../../../src/utils/uuid';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Pressable,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import DatePickerModal from '../../../src/components/DatePickerModal';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { goalsApi, visionsApi, activitiesApi, Goal, Vision, Activity } from '../../../src/api/client';
import { entityMutation } from '../../../src/api/queryClient';
import { deriveActivityStatus, deriveGoalStatus } from '../../../src/utils/displayStatus';
import { encrypt, decrypt, encryptTitle, mergeEncryptionMetadata, decryptEntityTitle, getKeyForTeam } from '../../../src/utils/encryption';
import { formatDisplayDateLong, toOffsetIso } from '../../../src/utils/format';
import { GOAL_STATUS_COLORS } from '../../../src/theme';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import Icon from '../../../src/components/Icon';
import AiSuggestionsCard from '../../../src/components/AiSuggestionsCard';
import AiUnavailableBanner from '../../../src/components/AiUnavailableBanner';
import { useOnDeviceLlm } from '../../../src/hooks/useOnDeviceLlm';
import type { SuggestionItem, ActivityCoverageResult } from '../../../src/services/llm';
import {
  Measure,
  computeMilestoneTarget,
  computeMilestoneDates,
  getCurrentMilestoneIndex,
  resetMilestoneAchievements,
  countRecordedMilestones,
  initializeMilestones,
} from '../../../src/utils/milestones';
import ScreenHeader from '../../../src/components/ScreenHeader';

const STATUS_OPTIONS = [
  { key: 'planned', label: 'Planned' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'paused', label: 'Paused' },
] as const;

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  centered: { flex: 1, justifyContent: 'center' as const, alignItems: 'center' as const, backgroundColor: theme.colors.bgSecondary },
  content: { flex: 1 },
  contentInner: { padding: 16, paddingBottom: 40 },
  title: { fontSize: 22, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 16 },
  titleInput: { fontSize: 22, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 16, borderBottomWidth: 2, borderBottomColor: theme.colors.primary, paddingBottom: 8 },
  progressCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginBottom: 8, ...theme.shadows.sm },
  progressLabel: { fontSize: 14, color: theme.colors.textSecondary, fontWeight: '600' as const, marginBottom: 10 },
  progressTrack: { height: 8, backgroundColor: theme.colors.bgTertiary, borderRadius: 4, overflow: 'hidden' as const },
  progressFill: { height: '100%' as const, backgroundColor: theme.colors.primary, borderRadius: 4 },
  sectionLabel: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 8, marginTop: 16 },
  statusRow: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 8 },
  statusChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, backgroundColor: theme.colors.bgTertiary },
  statusChipText: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textMuted },
  infoCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, ...theme.shadows.sm },
  infoRow: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, paddingVertical: 4 },
  infoLabel: { fontSize: 14, fontWeight: '500' as const, color: theme.colors.textMuted },
  infoValue: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textPrimary },
  infoText: { fontSize: 15, color: theme.colors.textSecondary },
  divider: { height: 1, backgroundColor: theme.colors.bgTertiary, marginVertical: 10 },
  descriptionText: { fontSize: 15, color: theme.colors.textSecondary, lineHeight: 22 },
  descriptionInput: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, fontSize: 15, color: theme.colors.textPrimary, minHeight: 100, borderWidth: 1, borderColor: theme.colors.border },
  activityItem: { flexDirection: 'row' as const, alignItems: 'center' as const, backgroundColor: theme.colors.background, borderRadius: 10, padding: 14, marginBottom: 6, gap: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.03, shadowRadius: 3, elevation: 1 },
  activityStatusIcon: { fontSize: 16, color: theme.colors.primary },
  activityTitle: { fontSize: 14, color: theme.colors.textPrimary, flex: 1 },
  activityTitleDone: { textDecorationLine: 'line-through' as const, color: theme.colors.textDisabled },
  moreLink: { fontSize: 14, color: theme.colors.primary, fontWeight: '600' as const, marginTop: 4 },
  measureCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginBottom: 8, ...theme.shadows.sm },
  measureCardHeader: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, marginBottom: 12 },
  measureCardTitle: { fontSize: 16, fontWeight: '700' as const, color: theme.colors.textPrimary },
  measureCardMeta: { fontSize: 13, color: theme.colors.textMuted, fontWeight: '500' as const },
  milestoneItem: { borderTopWidth: 1, borderTopColor: theme.colors.borderLight, paddingVertical: 10 },
  milestoneHeader: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, marginBottom: 4 },
  milestoneIndexRow: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 6 },
  milestoneIndex: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textPrimary },
  milestoneDates: { fontSize: 12, color: theme.colors.textMuted },
  milestoneValues: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const },
  milestoneTarget: { fontSize: 13, color: theme.colors.textSecondary },
  milestoneAchieved: { fontSize: 13, fontWeight: '600' as const },
  milestoneGood: { color: theme.colors.success },
  milestoneBehind: { color: theme.colors.warning },
  milestoneFuture: { fontSize: 13, color: theme.colors.textDisabled, fontStyle: 'italic' as const },
  achievementInputRow: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 8 },
  achievementInput: { backgroundColor: theme.colors.bgSecondary, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, fontSize: 14, color: theme.colors.textPrimary, width: 80 },
  recordBtn: { backgroundColor: theme.colors.primary, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7 },
  recordBtnText: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.background },
  editMeasuresContainer: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: theme.colors.border },
  editMeasureRow: { marginBottom: 12 },
  editMeasureHeader: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, marginBottom: 8 },
  editMeasureLabel: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textSecondary },
  editRemoveText: { fontSize: 13, color: theme.colors.error, fontWeight: '500' as const },
  editMeasureFields: { flexDirection: 'row' as const, gap: 8, marginBottom: 8 },
  editMeasureInput: { backgroundColor: theme.colors.bgSecondary, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: theme.colors.textPrimary },
  editMilestoneRow: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 12, marginBottom: 8 },
  editStepperBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: theme.colors.primaryBg, alignItems: 'center' as const, justifyContent: 'center' as const },
  editStepperText: { fontSize: 20, fontWeight: '600' as const, color: theme.colors.primary },
  editMilestoneValue: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textPrimary, minWidth: 30, textAlign: 'center' as const },
  editMilestoneHint: { fontSize: 13, color: theme.colors.textMuted, flex: 1 },
  editAddMeasureBtn: { alignItems: 'center' as const, paddingVertical: 12, borderWidth: 1, borderColor: theme.colors.primary, borderRadius: 10, borderStyle: 'dashed' as const, marginBottom: 8 },
  editAddMeasureText: { fontSize: 14, color: theme.colors.primary, fontWeight: '500' as const },
  selector: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14 },
  selectorText: { fontSize: 15, color: theme.colors.textSecondary, flex: 1 },
  chevron: { fontSize: 12, color: theme.colors.textDisabled, marginLeft: 8 },
  pickerList: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, marginTop: 4, overflow: 'hidden' as const },
  pickerItem: { paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.colors.borderLight },
  pickerItemActive: { backgroundColor: theme.colors.primaryBg },
  pickerItemText: { fontSize: 14, color: theme.colors.textSecondary },
  dateRow: { flexDirection: 'row' as const, gap: 12 },
  dateField: { flex: 1 },
  dateLabel: { fontSize: 13, fontWeight: '500' as const, color: theme.colors.textSecondary, marginBottom: 6 },
  dateInput: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12 },
  dateInputText: { fontSize: 14, color: theme.colors.textPrimary, fontWeight: '500' as const },
  aiButton: { backgroundColor: theme.colors.aiBg, borderWidth: 1, borderColor: theme.colors.aiBorder, borderRadius: 10, paddingVertical: 12, alignItems: 'center' as const, marginTop: 16 },
  aiButtonText: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.aiText },
  coverageCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginTop: 10, ...theme.shadows.sm },
  coverageDimRow: { flexDirection: 'row' as const, alignItems: 'center' as const, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
  coverageStatusDot: { width: 10, height: 10, borderRadius: 5, marginRight: 10 },
  coverageDimContent: { flex: 1, marginRight: 8 },
  coverageDimName: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textPrimary },
  coverageDimDetail: { fontSize: 12, color: theme.colors.textMuted, marginTop: 2 },
  coverageSummary: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 12, lineHeight: 18, fontStyle: 'italic' as const },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  statusBadgeText: { fontSize: 11, fontWeight: '700' as const, textTransform: 'uppercase' as const },
});

export default function GoalDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const [goal, setGoal] = useState<Goal | null>(null);
  const [vision, setVision] = useState<Vision | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSaving, setSaving] = useState(false);
  const [visionTitle, setVisionTitle] = useState('');
  const [activityTitles, setActivityTitles] = useState<Record<string, string>>({});
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [measures, setMeasures] = useState<Measure[]>([]);
  const [achievementInputs, setAchievementInputs] = useState<Record<string, string>>({});
  const [isSavingAchievement, setIsSavingAchievement] = useState(false);
  // Edit-mode state for measures
  const [editMeasures, setEditMeasures] = useState<Array<{
    id: string;
    name: string;
    unit: string;
    start_value: string;
    target_value: string;
  }>>([]);
  const [editMilestoneCount, setEditMilestoneCount] = useState<number | null>(null);
  const [editVisionId, setEditVisionId] = useState<string | undefined>();
  const [editStartDate, setEditStartDate] = useState<string | null>(null);
  const [editEndDate, setEditEndDate] = useState<string | null>(null);
  const [visions, setVisions] = useState<Vision[]>([]);
  const [visionTitles, setVisionTitles] = useState<Record<string, string>>({});
  const [showVisionPicker, setShowVisionPicker] = useState(false);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const llm = useOnDeviceLlm();
  const [aiSuggestions, setAiSuggestions] = useState<SuggestionItem[]>([]);
  const [showAiResults, setShowAiResults] = useState(false);
  const [coverageResult, setCoverageResult] = useState<ActivityCoverageResult | null>(null);
  const [showCoverage, setShowCoverage] = useState(false);
  const [creatingGapActivities, setCreatingGapActivities] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (!id || id === 'create') return;
      fetchGoal();
    }, [id])
  );

  const fetchGoal = async () => {
    setIsLoading(true);
    try {
      const res = await goalsApi.get(id!);
      setGoal(res.data);
      const plainTitle = await decryptEntityTitle(res.data);
      setEditTitle(plainTitle);
      // Decrypt description
      const key = await getKeyForTeam(res.data.team_id);
      if (res.data.description_encrypted && res.data.encryption_metadata?.iv) {
        try {
          const plainDesc = await decrypt(res.data.description_encrypted, { iv: res.data.encryption_metadata.iv }, key);
          setEditDescription(plainDesc);
        } catch { setEditDescription(''); }
      } else {
        setEditDescription(res.data.description_encrypted || '');
      }
      // Decrypt dates
      if (res.data.start_date_encrypted && res.data.encryption_metadata?.start_date_iv) {
        try { setStartDate(await decrypt(res.data.start_date_encrypted, { iv: res.data.encryption_metadata.start_date_iv }, key)); } catch {}
      }
      if (res.data.end_date_encrypted && res.data.encryption_metadata?.end_date_iv) {
        try { setEndDate(await decrypt(res.data.end_date_encrypted, { iv: res.data.encryption_metadata.end_date_iv }, key)); } catch {}
      }
      // Decrypt measures
      if (res.data.metrics_encrypted && res.data.encryption_metadata?.metrics_iv) {
        try {
          const metricsJson = await decrypt(res.data.metrics_encrypted, { iv: res.data.encryption_metadata.metrics_iv }, key);
          setMeasures(JSON.parse(metricsJson));
        } catch { setMeasures([]); }
      } else {
        setMeasures([]);
      }

      if (res.data.vision_id) {
        try {
          const vRes = await visionsApi.get(res.data.vision_id);
          setVision(vRes.data);
          setVisionTitle(await decryptEntityTitle(vRes.data));
        } catch {}
      }

      try {
        const tRes = await activitiesApi.list({ goal_id: id });
        const activityList = tRes.data;
        setActivities(activityList);
        // Decrypt activity titles
        const titleEntries = await Promise.all(
          activityList.map(async (t) => [t.id, await decryptEntityTitle(t)] as const)
        );
        setActivityTitles(Object.fromEntries(titleEntries));
      } catch {}
    } catch {
      showAlert('Error', 'Failed to load goal.', () => router.back());
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!goal) return;
    try {
      const res = await entityMutation(() => goalsApi.updateStatus(goal.id, status));
      setGoal(res.data);
    } catch {
      showAlert('Error', 'Failed to update status.');
    }
  };

  const handleSave = async () => {
    if (!requireConnectivity()) return;
    if (!goal || !editTitle.trim()) return;
    setSaving(true);
    try {
      const key = await getKeyForTeam(goal.team_id);
      const { title_encrypted, title_iv } = await encryptTitle(editTitle.trim(), key);
      let descEncrypted: string | undefined;
      let descIv: string | undefined;
      if (editDescription.trim()) {
        const descResult = await encrypt(editDescription.trim(), key);
        descEncrypted = descResult.encrypted;
        descIv = descResult.metadata.iv;
      }

      // Build updated measures
      const validMeasures = editMeasures.filter(
        (m) => m.name.trim() && m.unit.trim() && m.start_value && m.target_value
      );
      let metricsEncrypted: string | undefined;
      let metricsIv: string | undefined;
      const effectiveMilestoneCount = validMeasures.length > 0 ? editMilestoneCount : null;

      if (validMeasures.length > 0) {
        // Preserve existing milestone achievements for measures that still exist
        const existingMap = new Map(measures.map((m) => [m.id, m]));
        const updatedMeasures: Measure[] = validMeasures.map((m) => {
          const existing = existingMap.get(m.id);
          const msCount = effectiveMilestoneCount ?? 0;
          let milestones = existing?.milestones ?? [];
          // Re-initialize milestones if count changed or measure is new
          if (!existing || milestones.length !== msCount) {
            // Preserve achievements that still fit within the new count
            const preserved = milestones.filter((ms) => ms.milestone_index < msCount);
            milestones = msCount > 0
              ? initializeMilestones(msCount).map((init) => {
                  const prev = preserved.find((p) => p.milestone_index === init.milestone_index);
                  return prev ?? init;
                })
              : [];
          }
          return {
            id: m.id,
            name: m.name.trim(),
            unit: m.unit.trim(),
            start_value: parseFloat(m.start_value),
            target_value: parseFloat(m.target_value),
            milestones,
          };
        });
        const metricsResult = await encrypt(JSON.stringify(updatedMeasures), key);
        metricsEncrypted = metricsResult.encrypted;
        metricsIv = metricsResult.metadata.iv;
      }

      // Encrypt dates
      let startDateEncrypted: string | undefined;
      let startDateIv: string | undefined;
      let endDateEncrypted: string | undefined;
      let endDateIv: string | undefined;
      if (editStartDate) {
        const startResult = await encrypt(editStartDate, key);
        startDateEncrypted = startResult.encrypted;
        startDateIv = startResult.metadata.iv;
      }
      if (editEndDate) {
        const endResult = await encrypt(editEndDate, key);
        endDateEncrypted = endResult.encrypted;
        endDateIv = endResult.metadata.iv;
      }

      const res = await entityMutation(() =>
        goalsApi.update(goal.id, {
          title_encrypted,
          description_encrypted: descEncrypted,
          vision_id: editVisionId ?? null,
          start_date_encrypted: startDateEncrypted,
          end_date_encrypted: endDateEncrypted,
          encryption_metadata: mergeEncryptionMetadata(descIv, title_iv, {
            ...(startDateIv ? { start_date_iv: startDateIv } : goal.encryption_metadata?.start_date_iv ? { start_date_iv: goal.encryption_metadata.start_date_iv } : {}),
            ...(endDateIv ? { end_date_iv: endDateIv } : goal.encryption_metadata?.end_date_iv ? { end_date_iv: goal.encryption_metadata.end_date_iv } : {}),
            ...(metricsIv ? { metrics_iv: metricsIv } : goal.encryption_metadata?.metrics_iv ? { metrics_iv: goal.encryption_metadata.metrics_iv } : {}),
          }),
          metrics_encrypted: metricsEncrypted,
          milestone_count: effectiveMilestoneCount ?? undefined,
        }),
      );
      setGoal(res.data);
      // Update local date state
      if (editStartDate) setStartDate(editStartDate);
      if (editEndDate) setEndDate(editEndDate);
      // Update local vision state
      if (editVisionId) {
        const matchedVision = visions.find((v) => v.id === editVisionId);
        if (matchedVision) {
          setVision(matchedVision);
          setVisionTitle(visionTitles[editVisionId] || '');
        }
      } else {
        setVision(null);
        setVisionTitle('');
      }
      // Re-decrypt measures from the saved response
      if (metricsEncrypted && metricsIv) {
        const metricsJson = await decrypt(metricsEncrypted, { iv: metricsIv }, key);
        setMeasures(JSON.parse(metricsJson));
      } else if (validMeasures.length === 0) {
        setMeasures([]);
      }
      setIsEditing(false);
    } catch {
      showAlert('Error', 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async () => {
    if (!goal) return;
    try {
      const cloneTitle = `${editTitle} (copy)`;
      const key = await getKeyForTeam(goal.team_id);
      const { title_encrypted, title_iv } = await encryptTitle(cloneTitle, key);

      // Reset milestone achievements for the clone
      let cloneMetricsEncrypted: string | undefined;
      let cloneMetricsIv: string | undefined;
      if (measures.length > 0) {
        const resetMeasures = resetMilestoneAchievements(measures);
        const metricsResult = await encrypt(JSON.stringify(resetMeasures), key);
        cloneMetricsEncrypted = metricsResult.encrypted;
        cloneMetricsIv = metricsResult.metadata.iv;
      }

      await entityMutation(() =>
        goalsApi.create({
          title_encrypted,
          description_encrypted: goal.description_encrypted || undefined,
          encryption_metadata: mergeEncryptionMetadata(goal.encryption_metadata?.iv, title_iv, {
            ...(cloneMetricsIv ? { metrics_iv: cloneMetricsIv } : goal.encryption_metadata?.metrics_iv ? { metrics_iv: goal.encryption_metadata.metrics_iv } : {}),
            ...(goal.encryption_metadata?.start_date_iv && { start_date_iv: goal.encryption_metadata.start_date_iv }),
            ...(goal.encryption_metadata?.end_date_iv && { end_date_iv: goal.encryption_metadata.end_date_iv }),
          }),
          vision_id: goal.vision_id || undefined,
          start_date_encrypted: goal.start_date_encrypted || undefined,
          end_date_encrypted: goal.end_date_encrypted || undefined,
          metrics_encrypted: cloneMetricsEncrypted || (goal.metrics_encrypted || undefined),
          milestone_count: goal.milestone_count ?? undefined,
          status: 'planned',
        }),
      );
      showAlert('Cloned', 'Goal duplicated successfully.', () => router.back());
    } catch {
      showAlert('Error', 'Failed to clone goal.');
    }
  };

  /** Enter edit mode: seed the edit fields from what is on screen, and fetch
   *  the visions list the picker needs. */
  const handleEditPress = () => {
    setEditMeasures(measures.map((m) => ({
      id: m.id,
      name: m.name,
      unit: m.unit,
      start_value: String(m.start_value),
      target_value: String(m.target_value),
    })));
    setEditMilestoneCount(goal?.milestone_count ?? null);
    setEditVisionId(goal?.vision_id ?? undefined);
    setEditStartDate(startDate);
    setEditEndDate(endDate);
    setShowVisionPicker(false);
    visionsApi.list().then(async (res) => {
      setVisions(res.data);
      const entries = await Promise.all(
        res.data.map(async (v): Promise<[string, string]> => [v.id, await decryptEntityTitle(v)])
      );
      setVisionTitles(Object.fromEntries(entries));
    }).catch(() => {});
    setIsEditing(true);
  };

  const handleDelete = () => {
    if (!requireConnectivity()) return;
    showConfirm(
      'Delete Goal',
      `Delete "${editTitle}"? Activities linked to this goal will be unlinked.`,
      async () => {
        try {
          await entityMutation(() => goalsApi.delete(goal!.id));
          router.back();
        } catch {
          showAlert('Error', 'Failed to delete goal.');
        }
      },
      { confirmText: 'Delete', destructive: true }
    );
  };


  const handleAiSuggest = async () => {
    if (!goal) return;
    setShowAiResults(true);
    setAiSuggestions([]);
    const result = await llm.suggest('goal', `${editTitle.trim()}: ${editDescription.trim()}`);
    if (result) {
      setAiSuggestions(result.suggestions);
    }
  };

  const handleAnalyzeActivityCoverage = async () => {
    if (!goal) return;
    setShowCoverage(true);
    setCoverageResult(null);
    const titles = activities.map((a) => activityTitles[a.id] || 'Untitled');
    const result = await llm.analyzeActivityCoverage(editTitle.trim(), editDescription.trim(), titles);
    if (result) setCoverageResult(result);
  };

  const handleCreateGapActivities = async () => {
    if (!requireConnectivity()) return;
    if (!goal || !coverageResult) return;
    const gapDimensions = coverageResult.dimensions
      .filter((d) => d.status === 'gap')
      .map((d) => d.name);
    if (gapDimensions.length === 0) return;

    setCreatingGapActivities(true);
    try {
      const generatedActivities = await llm.generateGapActivities(editTitle.trim(), editDescription.trim(), gapDimensions);
      if (!generatedActivities || generatedActivities.length === 0) {
        showAlert('Error', 'AI could not generate activities. Please try again.');
        return;
      }

      const key = await getKeyForTeam(goal.team_id);

      for (const activity of generatedActivities) {
        const { title_encrypted, title_iv } = await encryptTitle(activity.title, key);
        const descResult = await encrypt(activity.description, key);
        const estMinResult = await encrypt(String(activity.estimated_minutes), key);

        // Calculate start_time from goal start date + relative_start_days
        const baseDate = startDate ? new Date(startDate) : new Date();
        baseDate.setDate(baseDate.getDate() + activity.relative_start_days);
        const startTime = toOffsetIso(baseDate, '09:00');

        await entityMutation(() => activitiesApi.create({
          title_encrypted,
          description_encrypted: descResult.encrypted,
          estimated_minutes_encrypted: estMinResult.encrypted,
          encryption_metadata: mergeEncryptionMetadata(descResult.metadata.iv, title_iv, {
            estimated_minutes_iv: estMinResult.metadata.iv,
          }),
          goal_id: goal.id,
          start_time: startTime,
        }));
      }

      await fetchGoal();
      setShowCoverage(false);
      setCoverageResult(null);
      showAlert('Activities Created', `${generatedActivities.length} activit${generatedActivities.length > 1 ? 'ies' : 'y'} created to cover the gaps.`);
    } catch {
      showAlert('Error', 'Failed to create activities. Please try again.');
    } finally {
      setCreatingGapActivities(false);
    }
  };

  const handleRecordAchievement = async (measureId: string, milestoneIndex: number) => {
    if (!goal) return;
    const inputKey = `${measureId}-${milestoneIndex}`;
    const rawValue = achievementInputs[inputKey];
    if (!rawValue?.trim()) return;
    const value = parseFloat(rawValue);
    if (isNaN(value)) { showAlert('Invalid', 'Please enter a valid number.'); return; }

    setIsSavingAchievement(true);
    try {
      const updatedMeasures = measures.map((m) => {
        if (m.id !== measureId) return m;
        return {
          ...m,
          milestones: m.milestones.map((ms) =>
            ms.milestone_index === milestoneIndex
              ? { ...ms, achieved_value: value, recorded_at: new Date().toISOString() }
              : ms
          ),
        };
      });
      const key = await getKeyForTeam(goal.team_id);
      const metricsResult = await encrypt(JSON.stringify(updatedMeasures), key);
      const res = await entityMutation(() =>
        goalsApi.update(goal.id, {
          metrics_encrypted: metricsResult.encrypted,
          encryption_metadata: mergeEncryptionMetadata(
            goal.encryption_metadata?.iv,
            goal.encryption_metadata?.title_iv,
            {
              metrics_iv: metricsResult.metadata.iv,
              ...(goal.encryption_metadata?.start_date_iv && { start_date_iv: goal.encryption_metadata.start_date_iv }),
              ...(goal.encryption_metadata?.end_date_iv && { end_date_iv: goal.encryption_metadata.end_date_iv }),
            }
          ),
        }),
      );
      setGoal(res.data);
      setMeasures(updatedMeasures);
      setAchievementInputs((prev) => { const next = { ...prev }; delete next[inputKey]; return next; });
    } catch {
      showAlert('Error', 'Failed to save achievement.');
    } finally {
      setIsSavingAchievement(false);
    }
  };

  const addEditMeasure = useCallback(() => {
    setEditMeasures((prev) => [
      ...prev,
      { id: randomUUID(), name: '', unit: '', start_value: '', target_value: '' },
    ]);
  }, []);

  const updateEditMeasure = useCallback((id: string, field: string, value: string) => {
    setEditMeasures((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  }, []);

  const removeEditMeasure = useCallback((id: string) => {
    setEditMeasures((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // Compute milestone periods if dates and milestone count are available
  const milestoneCount = goal?.milestone_count ?? null;
  const milestonePeriods = startDate && endDate && milestoneCount
    ? computeMilestoneDates(new Date(startDate), new Date(endDate), milestoneCount)
    : null;
  const currentMilestoneIdx = startDate && endDate && milestoneCount
    ? getCurrentMilestoneIndex(new Date(startDate), new Date(endDate), milestoneCount)
    : -1;

  const activeActivities = activities;
  const resolvedActivities = activeActivities.filter((t) => t.status === 'done' || t.status === 'skipped').length;
  const progress = activeActivities.length > 0 ? Math.round((resolvedActivities / activeActivities.length) * 100) : 0;

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!goal) return null;

  // planned/active follows the decrypted start date; see displayStatus.ts.
  const displayStatus = deriveGoalStatus(goal.status, startDate);

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="Goal Details"
        left={
          isEditing
            ? {
                label: 'Cancel editing',
                text: 'Cancel',
                onPress: () => setIsEditing(false),
                testID: 'goal-detail.back-button',
              }
            : {
                label: 'Go back',
                icon: 'arrow-back',
                onPress: () => router.back(),
                testID: 'goal-detail.back-button',
              }
        }
        right={
          isEditing
            ? {
                label: 'Save changes',
                text: 'Save',
                tone: 'primary',
                onPress: handleSave,
                disabled: isSaving,
                loading: isSaving,
                testID: 'goal-detail.save-button',
              }
            : [
                {
                  label: 'Clone goal',
                  icon: 'copy-outline',
                  onPress: handleClone,
                  testID: 'goal-detail.clone-button',
                },
                {
                  label: 'Edit goal',
                  icon: 'create-outline',
                  onPress: handleEditPress,
                  testID: 'goal-detail.edit-button',
                },
                {
                  label: 'Delete goal',
                  icon: 'trash-outline',
                  tone: 'danger',
                  accessibilityHint: 'Permanently deletes this goal',
                  onPress: handleDelete,
                  testID: 'goal-detail.delete-button',
                },
              ]
        }
      />
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>
        {/* Title */}
        {isEditing ? (
          <TextInput
            style={styles.titleInput}
            value={editTitle}
            onChangeText={setEditTitle}
            placeholder="Goal title"
            autoFocus
            accessibilityLabel="Goal title"
            testID="goal-detail.title-input"
          />
        ) : (
          <Text style={styles.title}>{'\u{1F3AF}'} {editTitle}</Text>
        )}

        {/* Progress */}
        <View style={styles.progressCard}>
          <Text style={styles.progressLabel}>{progress}% complete · {resolvedActivities} of {activeActivities.length} activities resolved</Text>
          <View
            style={styles.progressTrack}
            accessibilityRole="progressbar"
            accessibilityValue={{ min: 0, max: 100, now: progress }}
          >
            <View style={[styles.progressFill, { width: `${progress}%` }]} accessible={false} />
          </View>
        </View>

        {/* Status */}
        <Text style={styles.sectionLabel}>STATUS</Text>
        <View style={styles.statusRow} accessibilityRole="radiogroup" accessibilityLabel="Goal status">
          {STATUS_OPTIONS.map((opt) => (
            <Pressable
              key={opt.key}
              style={[
                styles.statusChip,
                displayStatus === opt.key && { backgroundColor: GOAL_STATUS_COLORS[opt.key].bg },
              ]}
              onPress={() => handleStatusChange(opt.key)}
              accessibilityRole="button"
              accessibilityState={{ checked: displayStatus === opt.key, selected: displayStatus === opt.key }}
              aria-checked={displayStatus === opt.key}
              aria-selected={displayStatus === opt.key}
              accessibilityLabel={opt.label}
              testID={`goal-detail.status-${opt.key}`}
            >
              <Text style={[
                styles.statusChipText,
                displayStatus === opt.key && { color: GOAL_STATUS_COLORS[opt.key].text },
              ]} accessible={false}>
                {opt.label}
              </Text>
            </Pressable>
          ))}
        </View>

        {/* Linked Vision */}
        <Text style={styles.sectionLabel}>LINKED VISION</Text>
        {isEditing ? (
          <>
            <TouchableOpacity
              style={styles.selector}
              onPress={() => setShowVisionPicker(!showVisionPicker)}
              accessibilityLabel={editVisionId ? `Linked vision: ${visionTitles[editVisionId] || 'Untitled'}` : 'Select a vision (optional)'}
              accessibilityRole="button"
              testID="goal-detail.vision-selector"
            >
              <Text style={styles.selectorText} accessible={false}>
                {editVisionId ? `\u2728 ${visionTitles[editVisionId] || 'Untitled'}` : 'Select a vision (optional)'}
              </Text>
              <Text style={styles.chevron} accessible={false}>{showVisionPicker ? '\u25B2' : '\u25BC'}</Text>
            </TouchableOpacity>
            {showVisionPicker && (
              <View style={styles.pickerList}>
                <TouchableOpacity
                  style={[styles.pickerItem, !editVisionId && styles.pickerItemActive]}
                  onPress={() => { setEditVisionId(undefined); setShowVisionPicker(false); }}
                >
                  <Text style={styles.pickerItemText}>None</Text>
                </TouchableOpacity>
                {visions.map((v) => (
                  <TouchableOpacity
                    key={v.id}
                    style={[styles.pickerItem, editVisionId === v.id && styles.pickerItemActive]}
                    onPress={() => { setEditVisionId(v.id); setShowVisionPicker(false); }}
                  >
                    <Text style={styles.pickerItemText} numberOfLines={1}>{visionTitles[v.id] || 'Untitled'}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </>
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.infoText}>
              {vision ? `\u2728 ${visionTitle}` : 'No linked vision'}
            </Text>
          </View>
        )}

        {/* Timeline */}
        <Text style={styles.sectionLabel}>TIMELINE</Text>
        {isEditing ? (
          <>
            <View style={styles.dateRow}>
              <View style={styles.dateField}>
                <Text style={styles.dateLabel}>Start Date</Text>
                <TouchableOpacity
                  style={styles.dateInput}
                  onPress={() => setShowStartPicker(true)}
                  accessibilityLabel={`Start date: ${editStartDate ? formatDisplayDateLong(editStartDate) : 'Not set'}`}
                  accessibilityRole="button"
                  testID="goal-detail.start-date-selector"
                >
                  <Text style={styles.dateInputText} accessible={false}>{editStartDate ? formatDisplayDateLong(editStartDate) : 'Not set'}</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.dateField}>
                <Text style={styles.dateLabel}>End Date</Text>
                <TouchableOpacity
                  style={styles.dateInput}
                  onPress={() => setShowEndPicker(true)}
                  accessibilityLabel={`End date: ${editEndDate ? formatDisplayDateLong(editEndDate) : 'Not set'}`}
                  accessibilityRole="button"
                  testID="goal-detail.end-date-selector"
                >
                  <Text style={styles.dateInputText} accessible={false}>{editEndDate ? formatDisplayDateLong(editEndDate) : 'Not set'}</Text>
                </TouchableOpacity>
              </View>
            </View>
            <DatePickerModal
              visible={showStartPicker}
              value={editStartDate || ''}
              onSelect={(d) => { setEditStartDate(d); setShowStartPicker(false); }}
              onClose={() => setShowStartPicker(false)}
              title="Start Date"
            />
            <DatePickerModal
              visible={showEndPicker}
              value={editEndDate || ''}
              onSelect={(d) => { setEditEndDate(d); setShowEndPicker(false); }}
              onClose={() => setShowEndPicker(false)}
              title="End Date"
            />
          </>
        ) : (
          <View style={styles.infoCard}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Start Date</Text>
              <Text style={styles.infoValue}>{startDate ? formatDisplayDateLong(startDate) : 'Not set'}</Text>
            </View>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>End Date</Text>
              <Text style={styles.infoValue}>{endDate ? formatDisplayDateLong(endDate) : 'Not set'}</Text>
            </View>
          </View>
        )}

        {/* Measures & Milestones */}
        {isEditing ? (
          <>
            <Text style={styles.sectionLabel}>MEASURES & MILESTONES</Text>
            {editMeasures.length > 0 && (
              <View style={styles.editMeasuresContainer}>
                {editMeasures.map((measure, idx) => (
                  <View key={measure.id} style={styles.editMeasureRow}>
                    <View style={styles.editMeasureHeader}>
                      <Text style={styles.editMeasureLabel}>Measure {idx + 1}</Text>
                      <TouchableOpacity
                        onPress={() => removeEditMeasure(measure.id)}
                        accessibilityLabel={`Remove measure ${idx + 1}`}
                        testID={`goal-detail.remove-measure-${idx}`}
                      >
                        <Text style={styles.editRemoveText}>Remove</Text>
                      </TouchableOpacity>
                    </View>
                    <View style={styles.editMeasureFields}>
                      <TextInput
                        style={[styles.editMeasureInput, { flex: 2 }]}
                        value={measure.name}
                        onChangeText={(v) => updateEditMeasure(measure.id, 'name', v)}
                        placeholder="Name"
                        placeholderTextColor={colors.placeholder}
                        testID={`goal-detail.measure-name-${idx}`}
                      />
                      <TextInput
                        style={[styles.editMeasureInput, { flex: 1 }]}
                        value={measure.unit}
                        onChangeText={(v) => updateEditMeasure(measure.id, 'unit', v)}
                        placeholder="Unit"
                        placeholderTextColor={colors.placeholder}
                        testID={`goal-detail.measure-unit-${idx}`}
                      />
                    </View>
                    <View style={styles.editMeasureFields}>
                      <TextInput
                        style={[styles.editMeasureInput, { flex: 1 }]}
                        value={measure.start_value}
                        onChangeText={(v) => updateEditMeasure(measure.id, 'start_value', v)}
                        placeholder="Start"
                        placeholderTextColor={colors.placeholder}
                        keyboardType="numeric"
                        testID={`goal-detail.measure-start-${idx}`}
                      />
                      <TextInput
                        style={[styles.editMeasureInput, { flex: 1 }]}
                        value={measure.target_value}
                        onChangeText={(v) => updateEditMeasure(measure.id, 'target_value', v)}
                        placeholder="Target"
                        placeholderTextColor={colors.placeholder}
                        keyboardType="numeric"
                        testID={`goal-detail.measure-target-${idx}`}
                      />
                    </View>
                  </View>
                ))}

                {/* Milestone count stepper */}
                <Text style={[styles.editMeasureLabel, { marginTop: 4, marginBottom: 8 }]}>Milestones</Text>
                <View style={styles.editMilestoneRow}>
                  <TouchableOpacity
                    style={styles.editStepperBtn}
                    onPress={() => setEditMilestoneCount((c) => Math.max(1, (c ?? 4) - 1))}
                    accessibilityLabel="Decrease milestones"
                    testID="goal-detail.milestone-decrease"
                  >
                    <Text style={styles.editStepperText}>-</Text>
                  </TouchableOpacity>
                  <Text style={styles.editMilestoneValue} testID="goal-detail.milestone-count">
                    {editMilestoneCount ?? 4}
                  </Text>
                  <TouchableOpacity
                    style={styles.editStepperBtn}
                    onPress={() => setEditMilestoneCount((c) => Math.min(12, (c ?? 4) + 1))}
                    accessibilityLabel="Increase milestones"
                    testID="goal-detail.milestone-increase"
                  >
                    <Text style={styles.editStepperText}>+</Text>
                  </TouchableOpacity>
                  <Text style={styles.editMilestoneHint}>checkpoints</Text>
                </View>
              </View>
            )}
            <TouchableOpacity
              style={styles.editAddMeasureBtn}
              onPress={addEditMeasure}
              accessibilityLabel="Add a measure"
              testID="goal-detail.add-measure-button"
            >
              <Text style={styles.editAddMeasureText}>+ Add Measure</Text>
            </TouchableOpacity>
          </>
        ) : measures.length > 0 ? (
          <>
            <Text style={styles.sectionLabel}>MEASURES & MILESTONES</Text>
            {measures.map((measure) => (
              <View key={measure.id} style={styles.measureCard}>
                <View style={styles.measureCardHeader}>
                  <Text style={styles.measureCardTitle}>{measure.name}</Text>
                  <Text style={styles.measureCardMeta}>
                    {measure.start_value} {'\u2192'} {measure.target_value} {measure.unit}
                  </Text>
                </View>

                {milestonePeriods && milestoneCount ? (
                  milestonePeriods.map((period) => {
                    const target = computeMilestoneTarget(measure, period.index, milestoneCount);
                    const achievement = measure.milestones.find(
                      (ms) => ms.milestone_index === period.index
                    );
                    const hasAchievement = achievement?.achieved_value != null;
                    const isPastOrCurrent = period.index <= currentMilestoneIdx;
                    const inputKey = `${measure.id}-${period.index}`;

                    return (
                      <View key={period.index} style={styles.milestoneItem}>
                        <View style={styles.milestoneHeader}>
                          <View style={styles.milestoneIndexRow}>
                            <Icon
                              name={period.index === currentMilestoneIdx ? 'navigate' : hasAchievement ? 'flag' : 'flag-outline'}
                              size={16}
                              color={
                                period.index === currentMilestoneIdx ? colors.primary
                                : hasAchievement ? colors.success
                                : colors.textDisabled
                              }
                            />
                            <Text style={styles.milestoneIndex}>
                              Milestone {period.index + 1}
                            </Text>
                          </View>
                          <Text style={styles.milestoneDates}>
                            {formatDisplayDateLong(period.start.toISOString().slice(0, 10))} - {formatDisplayDateLong(period.end.toISOString().slice(0, 10))}
                          </Text>
                        </View>
                        <View style={styles.milestoneValues}>
                          <Text style={styles.milestoneTarget}>
                            Target: {target.toFixed(1)} {measure.unit}
                          </Text>
                          {hasAchievement ? (
                            <Text style={[
                              styles.milestoneAchieved,
                              // If decreasing target (e.g. weight loss), lower is better
                              measure.target_value < measure.start_value
                                ? (achievement!.achieved_value! <= target ? styles.milestoneGood : styles.milestoneBehind)
                                : (achievement!.achieved_value! >= target ? styles.milestoneGood : styles.milestoneBehind),
                            ]}>
                              Achieved: {achievement!.achieved_value} {measure.unit}
                            </Text>
                          ) : isPastOrCurrent ? (
                            <View style={styles.achievementInputRow}>
                              <TextInput
                                style={styles.achievementInput}
                                value={achievementInputs[inputKey] || ''}
                                onChangeText={(v) => setAchievementInputs((prev) => ({ ...prev, [inputKey]: v }))}
                                placeholder="Value"
                                placeholderTextColor={colors.placeholder}
                                keyboardType="numeric"
                                testID={`goal-detail.achievement-${measure.id}-${period.index}`}
                              />
                              <TouchableOpacity
                                style={styles.recordBtn}
                                onPress={() => handleRecordAchievement(measure.id, period.index)}
                                disabled={isSavingAchievement}
                                testID={`goal-detail.record-${measure.id}-${period.index}`}
                              >
                                <Text style={styles.recordBtnText}>
                                  {isSavingAchievement ? '...' : 'Record'}
                                </Text>
                              </TouchableOpacity>
                            </View>
                          ) : (
                            <Text style={styles.milestoneFuture}>Upcoming</Text>
                          )}
                        </View>
                      </View>
                    );
                  })
                ) : (
                  <Text style={styles.infoText}>
                    No milestones configured. Set milestone count and dates to track progress.
                  </Text>
                )}
              </View>
            ))}
          </>
        ) : null}

        {/* Description */}
        <Text style={styles.sectionLabel}>DESCRIPTION</Text>
        {isEditing ? (
          <TextInput
            style={styles.descriptionInput}
            value={editDescription}
            onChangeText={setEditDescription}
            placeholder="Add a description..."
            multiline
            textAlignVertical="top"
            accessibilityLabel="Goal description"
            testID="goal-detail.description-input"
          />
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.descriptionText}>
              {editDescription || 'No description'}
            </Text>
          </View>
        )}

        {/* AI Suggestions */}
        {llm.available === false && (
          <AiUnavailableBanner reason={llm.unavailableReason} />
        )}
        {llm.available === true && (
          <TouchableOpacity
            style={styles.aiButton}
            onPress={handleAiSuggest}
            disabled={llm.loading}
            accessibilityLabel="Get AI suggestions for goal"
            accessibilityRole="button"
            testID="goal-detail.ai-suggest-button"
          >
            <Text style={styles.aiButtonText}>{'\u{1F9E0}'} AI Suggest — Refine goal</Text>
          </TouchableOpacity>
        )}

        {showAiResults && (
          <AiSuggestionsCard
            loading={llm.loading}
            modelDownloading={llm.modelDownloading}
            suggestions={aiSuggestions}
            error={llm.error}
            onApplySuggestion={(text) => {
              setEditDescription(text);
              setIsEditing(true);
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
            onDismiss={() => {
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
          />
        )}

        {/* Activities */}
        <Text style={styles.sectionLabel}>ACTIVITIES ({activities.length})</Text>
        {activities.slice(0, 5).map((t) => (
          <TouchableOpacity
            key={t.id}
            style={styles.activityItem}
            onPress={() => router.push(`/(tabs)/activities/${t.id}` as any)}
          >
            <Text style={styles.activityStatusIcon}>
              {t.status === 'done' ? '\u25CF' : deriveActivityStatus(t) === 'in-progress' ? '\u25D0' : '\u25CB'}
            </Text>
            <Text style={[styles.activityTitle, t.status === 'done' && styles.activityTitleDone]} numberOfLines={1}>
              {activityTitles[t.id] || 'Untitled'}
            </Text>
          </TouchableOpacity>
        ))}
        {activities.length > 5 && (
          <Text style={styles.moreLink}>View all {activities.length} activities {'\u2192'}</Text>
        )}
        {activities.length === 0 && (
          <View style={styles.infoCard}>
            <Text style={styles.infoText}>No activities linked to this goal</Text>
          </View>
        )}

        {/* Activity Coverage — view mode only */}
        {!isEditing && (
        <>
        <Text style={styles.sectionLabel}>ACTIVITY COVERAGE</Text>
        {llm.available === true ? (
          <>
            <TouchableOpacity
              style={styles.aiButton}
              onPress={handleAnalyzeActivityCoverage}
              disabled={llm.loading}
              accessibilityLabel="Analyze activity coverage"
              accessibilityRole="button"
              testID="goal-detail.analyze-coverage-button"
            >
              <Text style={styles.aiButtonText}>{'\u{1F50D}'} {coverageResult ? 'Re-analyze Coverage' : 'Analyze Coverage'}</Text>
            </TouchableOpacity>
            {showCoverage && coverageResult ? (
              <View style={styles.coverageCard}>
                {coverageResult.dimensions.map((dim, i) => (
                  <View key={i} style={styles.coverageDimRow}>
                    <View style={[styles.coverageStatusDot, { backgroundColor: dim.status === 'covered' ? colors.success : colors.warning }]} />
                    <View style={styles.coverageDimContent}>
                      <Text style={styles.coverageDimName}>{dim.name}</Text>
                      <Text style={styles.coverageDimDetail}>
                        {dim.status === 'covered'
                          ? dim.activities.join(', ')
                          : 'No activities address this'}
                      </Text>
                    </View>
                    <View style={[styles.statusBadge, { backgroundColor: dim.status === 'covered' ? colors.success + '20' : colors.warning + '20' }]}>
                      <Text style={[styles.statusBadgeText, { color: dim.status === 'covered' ? colors.success : colors.warning }]}>
                        {dim.status === 'covered' ? 'COVERED' : 'GAP'}
                      </Text>
                    </View>
                  </View>
                ))}
                {coverageResult.summary ? (
                  <Text style={styles.coverageSummary}>{coverageResult.summary}</Text>
                ) : null}
                {coverageResult.dimensions.some((d) => d.status === 'gap') && (
                  <TouchableOpacity
                    style={[styles.aiButton, { marginTop: 12 }]}
                    onPress={handleCreateGapActivities}
                    disabled={creatingGapActivities || llm.loading}
                    accessibilityLabel="Create activities for coverage gaps"
                    accessibilityRole="button"
                    testID="goal-detail.create-gap-activities-button"
                  >
                    {creatingGapActivities ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <ActivityIndicator size="small" color={colors.aiText} />
                        <Text style={styles.aiButtonText}>Creating activities...</Text>
                      </View>
                    ) : (
                      <Text style={styles.aiButtonText}>{'\u{2728}'} Create Activities for Gaps</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            ) : showCoverage ? (
              <View style={styles.infoCard}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={[styles.infoText, { marginTop: 8, textAlign: 'center' as const }]}>Analyzing coverage...</Text>
              </View>
            ) : null}
          </>
        ) : null}
        </>
        )}

      </ScrollView>
    </View>
  );
}
